import { forwardRef, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { licenseApiService, type LicenseRequest } from '@/services/licenseApiService'

export interface DeclineLicenseRequestModalRef {
  open: (request: LicenseRequest) => void
}

interface DeclineLicenseRequestModalProps {
  onDeclined?: () => void
}

/**
 * Declines a customer's license request. The reason is required: it is the
 * only answer the customer sees.
 */
const DeclineLicenseRequestModal = forwardRef<DeclineLicenseRequestModalRef, DeclineLicenseRequestModalProps>(
  ({ onDeclined }, ref) => {
    const { t } = useTranslation(['requests', 'common'])
    const [isOpen, setIsOpen] = useState(false)
    const [target, setTarget] = useState<LicenseRequest | null>(null)
    const [reason, setReason] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState('')

    useImperativeHandle(ref, () => ({
      open: (request: LicenseRequest) => {
        setTarget(request)
        setReason('')
        setError('')
        setIsOpen(true)
      },
    }))

    const decline = async () => {
      if (!target || isLoading || !reason.trim()) return
      setIsLoading(true)
      setError('')
      try {
        await licenseApiService.declineLicenseRequest(target.id, reason.trim())
        setIsOpen(false)
        onDeclined?.()
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err))
      } finally {
        setIsLoading(false)
      }
    }

    const close = () => {
      if (!isLoading) setIsOpen(false)
    }

    if (!isOpen || !target) return null

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/30 backdrop-blur-md">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-red-600">{t('licenseRequests.decline.heading', 'Decline request')}</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {t('licenseRequests.decline.subtitle', {
                    account: target.requestor?.account ?? '-',
                    defaultValue: 'From {{account}}',
                  })}
                </p>
              </div>
              <button
                onClick={close}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isLoading}
                aria-label={t('common:close', 'Close')}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="p-6 space-y-4">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('licenseRequests.decline.reason', 'Reason, shown to the customer')} *
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={isLoading}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </div>

          <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={close}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              {t('common:cancel', 'Cancel')}
            </button>
            <button
              type="button"
              onClick={() => void decline()}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              disabled={isLoading || !reason.trim()}
            >
              {isLoading && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              )}
              <span>{t('licenseRequests.decline.confirm', 'Decline')}</span>
            </button>
          </div>
        </div>
      </div>
    )
  }
)

DeclineLicenseRequestModal.displayName = 'DeclineLicenseRequestModal'
export default DeclineLicenseRequestModal
